/**
 * 统一的时间格式化工具
 * 所有时间都转换为北京时间（Asia/Shanghai）
 */

/**
 * 格式化为北京时间字符串
 * @param dateStr - ISO 8601 日期字符串或数据库时间字符串
 * @param format - 格式类型
 * @returns 格式化后的北京时间字符串
 */
export function formatBeijingTime(
  dateStr: string | null | undefined,
  format: 'datetime' | 'date' | 'time' | 'full' = 'datetime'
): string {
  if (!dateStr) return '';
  
  try {
    // 确保时间字符串为合法 ISO 格式并包含时区信息
    let dt = dateStr.replace(' ', 'T');
    if (!/[Z+\-]\d{2}/.test(dt)) dt += '+08:00';
    const date = new Date(dt);
    if (isNaN(date.getTime())) return dateStr;
    
    // 转换为北京时间
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'Asia/Shanghai',
    };
    
    switch (format) {
      case 'datetime':
        // 格式: 2026-03-10 15:30
        return date.toLocaleString('zh-CN', {
          ...options,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).replace(/\//g, '-');
      
      case 'date':
        // 格式: 2026-03-10
        return date.toLocaleString('zh-CN', {
          ...options,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).replace(/\//g, '-');
      
      case 'time':
        // 格式: 15:30
        return date.toLocaleString('zh-CN', {
          ...options,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
      
      case 'full':
        // 格式: 2026-03-10 15:30:45 周一
        return date.toLocaleString('zh-CN', {
          ...options,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          weekday: 'short',
          hour12: false,
        }).replace(/\//g, '-');
      
      default:
        return dateStr;
    }
  } catch (error) {
    console.error('Date format error:', error, dateStr);
    return dateStr || '';
  }
}

/**
 * 格式化为相对时间（如：刚刚、5分钟前、2小时前）
 */
export function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  
  try {
    let dt = dateStr.replace(' ', 'T');
    if (!/[Z+\-]\d{2}/.test(dt)) dt += '+08:00';
    const date = new Date(dt);
    if (isNaN(date.getTime())) return dateStr || '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return '刚刚';
    if (diffMins < 60) return `${diffMins}分钟前`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}小时前`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}天前`;
    
    // 超过7天显示具体日期
    return formatBeijingTime(dateStr, 'datetime');
  } catch (error) {
    return dateStr || '';
  }
}
